// =============================================
// MusicPlayer — compact now-playing mini-bar (shown while browsing)
// =============================================
// A slim bar bound to the shared useMusicPlayer hook (the audio element lives in
// MusicPage). Appears when a track is loaded but the full Now-Playing page is closed,
// so playback continues while the user browses. Click it (or the expand control) to
// reopen the full player.
// =============================================
import React from 'react';
import { FiPlay, FiPause, FiSkipBack, FiSkipForward, FiMaximize2, FiX } from 'react-icons/fi';
import type { MusicPlayerApi } from './useMusicPlayer';
import MusicCover from './MusicCover';
import './MusicPlayer.css';

interface MusicPlayerProps {
  player: MusicPlayerApi;
  onExpand: () => void;
  onClose: () => void;
}

function fmtTime(sec: number): string {
  if (!isFinite(sec) || sec < 0) sec = 0;
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const MusicPlayer: React.FC<MusicPlayerProps> = ({ player, onExpand, onClose }) => {
  const track = player.track;
  if (!track) return null;
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <div className="mp-bar" onClick={onExpand} title="Open player">
      <div className="mp-track-info">
        <MusicCover coverKey={track.coverKey} size={40} className="mp-art" />
        <div className="mp-meta">
          <div className="mp-title">{track.title}</div>
          <div className="mp-artist">
            {player.missing ? <span className="mp-missing">Audio not on this device</span> : track.artist}
          </div>
        </div>
      </div>

      <div className="mp-controls">
        <div className="mp-buttons">
          <button className="mp-btn" onClick={(e) => { stop(e); player.prev(); }} disabled={!player.hasPrev && player.repeat !== 'all'} title="Previous"><FiSkipBack /></button>
          <button className="mp-btn mp-btn-play" onClick={(e) => { stop(e); player.togglePlay(); }} disabled={player.missing} title={player.isPlaying ? 'Pause' : 'Play'}>
            {player.isPlaying ? <FiPause /> : <FiPlay />}
          </button>
          <button className="mp-btn" onClick={(e) => { stop(e); player.next(); }} disabled={!player.hasNext && player.repeat !== 'all' && !player.shuffle} title="Next"><FiSkipForward /></button>
        </div>
        <div className="mp-seek">
          <span className="mp-time">{fmtTime(player.currentTime)}</span>
          <input
            className="mp-range" type="range" min={0} max={player.duration || 0} step={0.1}
            value={Math.min(player.currentTime, player.duration || 0)}
            onClick={stop}
            onChange={(e) => { player.seek(Number(e.target.value)); }}
            disabled={player.missing || !player.duration}
          />
          <span className="mp-time">{fmtTime(player.duration)}</span>
        </div>
      </div>

      <button className="mp-close" onClick={(e) => { stop(e); onExpand(); }} title="Open player"><FiMaximize2 /></button>
      <button className="mp-close" onClick={(e) => { stop(e); onClose(); }} title="Close player"><FiX /></button>
    </div>
  );
};

export default MusicPlayer;
